import { useEffect, useState, useCallback } from "react";
import { fetchIntegrations, syncIntegration } from "@/lib/integrations";
import { PageHeader } from "@/components/PageHeader";
import { fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import {
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Circle,
  FileSpreadsheet,
  FileText,
} from "lucide-react";

const KIND_META = {
  google_sheet: { label: "Google Sheet", Icon: FileSpreadsheet },
  google_doc: { label: "Google Doc", Icon: FileText },
};

function StatusBadge({ status }) {
  if (status === "ok") {
    return (
      <span
        data-testid="status-ok"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-700"
      >
        <CheckCircle2 size={12} strokeWidth={2.5} /> Synced
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        data-testid="status-error"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border border-rose-300 bg-rose-50 text-rose-700"
      >
        <AlertTriangle size={12} strokeWidth={2.5} /> Sync error
      </span>
    );
  }
  if (status === "not_configured") {
    return (
      <span
        data-testid="status-not-configured"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border border-amber-300 bg-amber-50 text-amber-800"
      >
        <AlertTriangle size={12} strokeWidth={2.5} /> Not configured
      </span>
    );
  }
  return (
    <span
      data-testid="status-never"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border border-[#141B4D]/20 bg-white text-[#141B4D]/60"
    >
      <Circle size={10} /> Not synced yet
    </span>
  );
}

function ResultSummary({ kind, result }) {
  if (!result || Object.keys(result).length === 0) return null;
  if (kind === "google_sheet") {
    const { inserted, updated, skipped, unmatched_beliefs } = result;
    return (
      <div className="text-xs text-[#141B4D]/70 mt-2 flex flex-wrap gap-x-4 gap-y-1">
        <span>
          <strong>{inserted ?? 0}</strong> new
        </span>
        <span>
          <strong>{updated ?? 0}</strong> updated
        </span>
        {skipped !== undefined && (
          <span>
            <strong>{skipped}</strong> skipped
          </span>
        )}
        {Array.isArray(unmatched_beliefs) && unmatched_beliefs.length > 0 && (
          <span className="text-amber-700">
            <strong>{unmatched_beliefs.length}</strong> unmatched belief refs
          </span>
        )}
      </div>
    );
  }
  if (kind === "google_doc") {
    return (
      <div className="text-xs text-[#141B4D]/70 mt-2">
        Fetched <strong>{result.chars?.toLocaleString() || 0}</strong> characters
      </div>
    );
  }
  return null;
}

function IntegrationCard({ integ, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const { Icon, label } = KIND_META[integ.kind] || {
    Icon: FileText,
    label: integ.kind,
  };

  const sync = async () => {
    setLoading(true);
    try {
      await syncIntegration(integ.key);
      toast.success(`${integ.name} synced.`);
      onRefresh?.();
    } catch (e) {
      toast.error(
        `${integ.name} sync failed: ${e?.response?.data?.detail || e.message}`,
      );
      onRefresh?.();
    } finally {
      setLoading(false);
    }
  };

  const isConfigured = integ.configured !== false;
  const hasViewUrl = !!integ.view_url;

  return (
    <article
      data-testid={`integration-card-${integ.key}`}
      className="bg-white border border-[#141B4D]/15 rounded-sm p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#141B4D]/5 text-[#141B4D] p-2 rounded-sm">
            <Icon size={18} strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#141B4D]/60">
              {label}
            </div>
            <h3 className="text-base font-bold text-[#141B4D]">{integ.name}</h3>
          </div>
        </div>
        <StatusBadge status={integ.status} />
      </div>

      <p className="text-sm text-[#141B4D]/75 leading-relaxed mb-4 flex-1">
        {integ.purpose}
      </p>

      <div className="text-xs text-[#141B4D]/60 mb-1">
        <strong>Last synced:</strong>{" "}
        <span data-testid={`last-synced-${integ.key}`}>
          {fmtRelative(integ.last_synced_at)}
        </span>
      </div>
      {integ.last_error && integ.status === "error" && (
        <div
          data-testid={`error-message-${integ.key}`}
          className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1.5 mt-2"
        >
          {integ.last_error}
        </div>
      )}
      {integ.last_error && integ.status === "not_configured" && (
        <div
          data-testid={`error-message-${integ.key}`}
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 mt-2"
        >
          {integ.last_error}
        </div>
      )}
      <ResultSummary kind={integ.kind} result={integ.result} />

      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#141B4D]/10">
        <button
          onClick={sync}
          disabled={loading || !isConfigured}
          data-testid={`sync-integration-${integ.key}`}
          className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#141B4D]/90 disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            !isConfigured
              ? "Set the sheet ID in backend/.env before you can sync"
              : "Sync this source now"
          }
        >
          <RefreshCw
            size={12}
            strokeWidth={2.5}
            className={loading ? "animate-spin" : ""}
          />
          {loading ? "Syncing…" : "Sync now"}
        </button>
        {hasViewUrl ? (
          <a
            href={integ.view_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`open-integration-${integ.key}`}
            className="inline-flex items-center gap-2 bg-white text-[#141B4D] border border-[#141B4D]/20 px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#141B4D]/5"
          >
            <ExternalLink size={12} strokeWidth={2.5} />
            Open in Drive
          </a>
        ) : (
          <span
            data-testid={`no-url-${integ.key}`}
            className="text-[10px] text-[#141B4D]/55 italic"
          >
            Add the sheet ID to make this link active
          </span>
        )}
      </div>
    </article>
  );
}

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchIntegrations();
      setIntegrations(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  const syncAll = async () => {
    setSyncingAll(true);
    let okCount = 0;
    let errCount = 0;
    let skippedCount = 0;
    for (const i of integrations) {
      if (i.configured === false) {
        skippedCount += 1;
        continue;
      }
      try {
        await syncIntegration(i.key);
        okCount += 1;
      } catch {
        errCount += 1;
      }
    }
    if (errCount === 0 && skippedCount === 0) {
      toast.success(`All ${okCount} integrations synced.`);
    } else if (errCount === 0) {
      toast.success(`${okCount} synced · ${skippedCount} skipped (not configured)`);
    } else {
      toast.error(`${okCount} synced, ${errCount} failed${skippedCount ? `, ${skippedCount} skipped` : ""}.`);
    }
    setSyncingAll(false);
    load();
  };

  const okCount = integrations.filter((i) => i.status === "ok").length;
  const errCount = integrations.filter((i) => i.status === "error").length;
  const neverCount = integrations.filter((i) => i.status === "never").length;

  return (
    <div data-testid="integrations-page">
      <PageHeader
        eyebrow="Connected sources"
        title="Integrations"
        subtitle="Every Google Drive file the dashboard reads from. Sync individually or all at once, see status at a glance, and jump straight to the source when you need to edit it."
        testId="integrations-header"
        actions={
          <button
            onClick={syncAll}
            disabled={syncingAll || loading}
            data-testid="sync-all-button"
            className="inline-flex items-center gap-2 bg-[#FFD100] text-[#141B4D] px-4 py-2 rounded-sm text-sm font-bold hover:bg-[#e6bd00] disabled:opacity-60"
          >
            <RefreshCw
              size={14}
              strokeWidth={2.5}
              className={syncingAll ? "animate-spin" : ""}
            />
            {syncingAll ? "Syncing all…" : "Sync all"}
          </button>
        }
      />

      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
        data-testid="integration-stats"
      >
        <StatTile
          label="Synced"
          value={okCount}
          accent="emerald"
          testId="stat-ok"
        />
        <StatTile
          label="Errors"
          value={errCount}
          accent="rose"
          testId="stat-error"
        />
        <StatTile
          label="Not synced yet"
          value={neverCount}
          accent="neutral"
          testId="stat-never"
        />
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-5"
        data-testid="integration-grid"
      >
        {integrations.map((i) => (
          <IntegrationCard key={i.key} integ={i} onRefresh={load} />
        ))}
        {!loading && integrations.length === 0 && (
          <div className="col-span-2 text-sm text-[#141B4D]/50 py-10 text-center border border-dashed border-[#141B4D]/20 rounded-sm">
            No integrations registered.
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent, testId }) {
  const accents = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    neutral: "bg-white border-[#141B4D]/15 text-[#141B4D]",
  };
  return (
    <div
      data-testid={testId}
      className={`border rounded-sm p-4 ${accents[accent] || accents.neutral}`}
    >
      <div className="text-[10px] uppercase tracking-[0.15em] opacity-70 mb-1">
        {label}
      </div>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}
